import {
  clearFiltersStorage,
  deleteAccessToken,
  getEnvVar,
} from '@zenos/front-end-util-utils';
import axios from 'axios';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { hasAuthSessionActive } from '@zenos/front-end-auth';
import App from './app/App.tsx';
import { isAuthRateLimited } from './app/auth/isAuthRateLimited.ts';
import { Providers } from './app/providers/Providers.tsx';
import { ZENOS_PATHS } from './app/router/paths/zenosPaths.ts';
import '@mantine/notifications/styles.css';

/** Auth routes can 401 when logged out; ProtectedRoute handles that without a reload. */
function isAuthSessionProbe(url: string | undefined): boolean {
  if (!url) return false;

  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    return (
      path === '/v1/auth' ||
      path === '/v1/auth/refresh' ||
      path === '/v1/auth/logout'
    );
  } catch {
    return false;
  }
}

async function main() {
  axios.defaults.baseURL = getEnvVar('apiUrl');
  axios.defaults.withCredentials = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        isAuthRateLimited(error?.response?.status, error?.config?.url) &&
        window.location.pathname !== ZENOS_PATHS.TOO_MANY_REQUESTS
      ) {
        setTimeout(
          () => (window.location.href = ZENOS_PATHS.TOO_MANY_REQUESTS),
          0,
        );
      } else if (error?.response?.status === 401) {
        // HttpOnly bootstrap: ProtectedRoute probes /auth/refresh when there is no
        // in-memory session. A 401 there must not hard-navigate to `/` or the page
        // reloads forever (same after logout).
        if (!isAuthSessionProbe(error?.config?.url)) {
          clearFiltersStorage();
          deleteAccessToken();
          setTimeout(() => (window.location.href = '/'), 0);
        }
      } else if (error?.response?.status === 403) {
        if (hasAuthSessionActive()) {
          if (window.location.pathname !== '/access-denied') {
            setTimeout(() => (window.location.href = '/access-denied'), 0);
          } else {
            clearFiltersStorage();
            deleteAccessToken();
            setTimeout(() => (window.location.href = '/'), 0);
          }
        } else {
          clearFiltersStorage();
          setTimeout(() => (window.location.href = '/'), 0);
        }
      }
      return Promise.reject(error);
    },
  );

  // For more info  see:
  // https://github.com/mswjs/msw/issues/1653
  const { router } = await import('./app/router/router.tsx');

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Providers>
        <App router={router} />
      </Providers>
    </React.StrictMode>,
  );
}

main();
